import {
  createFileRoute,
  Outlet,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";
import { useState } from "react";

import { PageWrapper } from "~/components/page-wrapper";
import { ReportsHeader } from "~/components/reports/header";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";
import { optionalSearchString } from "~/lib/parse-enum-value";

export const Route = createFileRoute("/_authenticated/reports")({
  component: ReportsLayout,
  validateSearch: (search: Record<string, unknown>) => ({
    type: optionalSearchString(search.type),
    location: optionalSearchString(search.location),
    groupBy: optionalSearchString(search.groupBy),
  }),
});

function ReportsLayout() {
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false });
  const { hasPermission, isLoading, hasEmployee } = useEmployeePermissions();
  const canViewAllOrders = hasPermission("viewAllOrders");

  const [selectedReportType, setSelectedReportType] = useState<
    string | undefined
  >(searchParams.type ?? undefined);
  const [selectedLocationId, setSelectedLocationId] = useState<
    string | undefined
  >(searchParams.location ?? undefined);

  const handleReportTypeChange = (reportType: string) => {
    setSelectedReportType(reportType);
    void navigate({
      to: `/reports/${reportType}`,
      search: {
        type: reportType,
        ...(selectedLocationId ? { location: selectedLocationId } : {}),
      },
    });
  };

  const handleLocationChange = (locationId: string) => {
    setSelectedLocationId(locationId);
    // Navigate to current report child route (or stay at /reports) with updated location
    const reportPath = selectedReportType
      ? `/reports/${selectedReportType}`
      : "/reports";
    void navigate({
      to: reportPath,
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        location: locationId,
      }),
    });
  };

  const handleGroupByChange = (groupBy: string) => {
    // Preserve the current child route path
    const reportPath = selectedReportType
      ? `/reports/${selectedReportType}`
      : "/reports";
    void navigate({
      to: reportPath,
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        groupBy,
      }),
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper
        description="View and analyze location performance data"
        title="Reports"
      >
        <Skeleton className="h-96 w-full" />
      </PageWrapper>
    );
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <PageWrapper
        description="View and analyze location performance data"
        title="Reports"
      >
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // No permission to view reports
  if (!canViewAllOrders) {
    return (
      <PageWrapper
        description="View and analyze location performance data"
        title="Reports"
      >
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to view reports. Contact your manager to
            request access.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      description="View and analyze location performance data"
      title="Reports"
    >
      <div className="space-y-6">
        <ReportsHeader
          onGroupByChange={handleGroupByChange}
          onLocationChange={handleLocationChange}
          onReportTypeChange={handleReportTypeChange}
          selectedLocationId={selectedLocationId}
          selectedReportType={selectedReportType}
        />

        {selectedReportType && selectedLocationId ? (
          <Outlet />
        ) : (
          <div className="flex h-64 items-center justify-center">
            <div className="text-center">
              <h3 className="subtitle text-foreground mb-2 text-balance">
                Select Report Type and Location
              </h3>
              <p className="body text-foreground">
                Choose a report type and location from the filters above to view
                data.
              </p>
            </div>
          </div>
        )}
      </div>
    </PageWrapper>
  );
}
